/**
 * `useNavCommandsFromAgent` — global navigation/chrome `lx_*` tools from
 * the concierge.
 *
 * Concierge slash-commands (Part A — LexIDE-target) per
 * `ai-docs/handoffs/concierge-slash-commands-lexide-tools-handoff-2026-06-30.md`
 * §2.1 (NAVIGATION). The concierge fires a `lx_<verb>` tool; the runtime's
 * near-no-op `execute()` succeeds; OpenClaw core auto-emits a `session.tool`
 * EventFrame with `phase: "start"`, `toolName`, `toolInput`; this hook
 * dispatches to the right store setter.
 *
 * Wires:
 *   • `lx_navigate`                 → router `navigate(mode→path)`; for
 *                                    `mode:"agent"` an optional `section`/`tab`
 *                                    routes to `/agent?section=<tab>` (the
 *                                    channel tab where Connect lives)
 *   • `lx_show_command_palette`     → `useCommandPaletteStore.setOpen(true)`
 *   • `lx_toggle_right_sidebar`     → `useUIStore.setRightSidebarExpanded(open?? toggle)`
 *   • `lx_toggle_sidebar`           → `useUIStore.setSidePanelCollapsed(!(open?? toggle))`
 *                                    (the store field is `sidePanelCollapsed` —
 *                                    invert to honor the tool's `open` semantics)
 *   • `lx_show_shortcuts`           → `useShortcutHelpStore.setOpen(true)`
 *
 * Mount once at the App level (global chrome — works from any route) — see
 * `App.tsx`'s AppShell mount block, alongside `useTeamPaneCommandsFromAgent`.
 *
 * Contract (frozen by the runtime — DO NOT reshape):
 *   • Act on `phase === "start"` only (else double-fire on the runtime's
 *     `end` echo — matches the shipped hooks' shape).
 *   • Accept `data.toolName ?? data.name` and `data.toolInput ?? data.args`
 *     (legacy field-name tolerance per useTeamPaneCommandsFromAgent).
 *   • The pure handler `handleNavCommand` is extracted for unit tests; the
 *     React wrapper just builds the deps + subscribes.
 */

import { useNavigate } from "react-router-dom";
import { useEventStream } from "./useEventStream";
import { useUIStore, type AppMode } from "@/stores/useUIStore";
import { useCommandPaletteStore } from "@/stores/useCommandPaletteStore";
import { useShortcutHelpStore } from "@/stores/useShortcutHelpStore";

export interface AgentEventPayloadRuntime {
  data?: {
    phase?: string;
    toolName?: string;
    /** Legacy field-name (some core paths emit `name`/`args`). */
    name?: string;
    toolInput?: Record<string, unknown>;
    args?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

/**
 * Mode → route path. MUST match `Sidebar.bottomItems`/`topItems`'s `path`
 * in `src/components/shell/ActivityBar.tsx`; the Sidebar derives
 * `activeMode` from the URL on every navigation (its `modeFromPath` +
 * the `useEffect` at L150-156), so calling `setActiveMode` directly here
 * would be reverted on the very next render. We navigate the router
 * instead and let the existing URL-derive effect propagate.
 *
 * `cases` is the catch-all when the lawyer hasn't picked a case yet —
 * `/workspace` is the empty-state listing page (per Sidebar.topItems).
 */
const MODE_TO_PATH: Record<AppMode, string> = {
  home: "/home",
  chat: "/chat",
  cases: "/workspace",
  contacts: "/contacts",
  tasks: "/tasks",
  // Agendamentos — like `/tasks`, a focus-pane surface reached by its route
  // entry (the rail toggles the pane; this path is the deep-link door).
  schedule: "/schedule",
  activity: "/activity",
  // `/agent` is a deep-link ENTRY that opens the focus pane and puts a chat on
  // the left (see AgentLayout). Routing here rather than special-casing keeps
  // this table a pure mode→path map; the entry does the pane work.
  agent: "/agent",
  agentBrowser: "/agent-browser",
  org: "/org",
  settings: "/settings",
};

function isAppMode(v: unknown): v is AppMode {
  return typeof v === "string" && v in MODE_TO_PATH;
}

/**
 * The agent page's tab ids (`AgentSurface`'s sections). Kept local — mirroring
 * `AgentSurface.isAgentSection` — to avoid pulling that heavy component module
 * into this App-level hook, the same trade `AgentChannelSummary` makes for its
 * local `ROUNDEL_TONE`. `AgentSurface` reads `?section=` and validates again on
 * its own side, so a value that slips past here is still refused downstream.
 */
const AGENT_SECTIONS: ReadonlySet<string> = new Set([
  "browser",
  "email",
  "telegram",
  "whatsapp",
  "settings",
]);

export interface NavCommandDeps {
  navigate: (path: string) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setRightSidebarExpanded: (expanded: boolean) => void;
  rightSidebarExpanded: boolean;
  setSidePanelCollapsed: (collapsed: boolean) => void;
  sidePanelCollapsed: boolean;
  setShortcutHelpOpen: (open: boolean) => void;
}

export function handleNavCommand(
  payload: unknown,
  deps: NavCommandDeps,
): void {
  const typed = payload as AgentEventPayloadRuntime;
  if (typed.data?.phase !== "start") return;
  const tool = typed.data?.toolName ?? typed.data?.name;
  const input = typed.data?.toolInput ?? typed.data?.args;

  switch (tool) {
    case "lx_navigate": {
      const mode = input?.mode;
      if (!isAppMode(mode)) return;
      // The agent page is TABBED (browser/email/telegram/whatsapp/settings). A
      // concierge steering the lawyer to "connect WhatsApp" wants the WhatsApp
      // tab — where the Connect button lives — not the agent default. So honor
      // an optional `section` (alias `tab`) and forward it as
      // `/agent?section=<tab>`, which `AgentSurface` reads. Absent or unknown →
      // the bare mode path, so a plain `lx_navigate { mode }` is unchanged.
      if (mode === "agent") {
        const section = input?.section ?? input?.tab;
        if (typeof section === "string" && AGENT_SECTIONS.has(section)) {
          deps.navigate(`/agent?section=${section}`);
          return;
        }
      }
      deps.navigate(MODE_TO_PATH[mode]);
      return;
    }
    case "lx_show_command_palette": {
      deps.setCommandPaletteOpen(true);
      return;
    }
    case "lx_toggle_right_sidebar": {
      const open = input?.open;
      const next = typeof open === "boolean" ? open : !deps.rightSidebarExpanded;
      deps.setRightSidebarExpanded(next);
      return;
    }
    case "lx_toggle_sidebar": {
      // Tool's `open` semantics is the user-visible OPEN state of the
      // side panel — the store stores the INVERTED collapsed bit.
      const open = input?.open;
      const nextOpen =
        typeof open === "boolean" ? open : deps.sidePanelCollapsed;
      deps.setSidePanelCollapsed(!nextOpen);
      return;
    }
    case "lx_show_shortcuts": {
      deps.setShortcutHelpOpen(true);
      return;
    }
    default:
      return;
  }
}

export function useNavCommandsFromAgent(): void {
  const navigate = useNavigate();
  const setCommandPaletteOpen = useCommandPaletteStore((s) => s.setOpen);
  const setRightSidebarExpanded = useUIStore((s) => s.setRightSidebarExpanded);
  const rightSidebarExpanded = useUIStore((s) => s.rightSidebarExpanded);
  const setSidePanelCollapsed = useUIStore((s) => s.setSidePanelCollapsed);
  const sidePanelCollapsed = useUIStore((s) => s.sidePanelCollapsed);
  const setShortcutHelpOpen = useShortcutHelpStore((s) => s.setOpen);

  useEventStream("session.tool", (payload) => {
    handleNavCommand(payload, {
      navigate,
      setCommandPaletteOpen,
      setRightSidebarExpanded,
      rightSidebarExpanded,
      setSidePanelCollapsed,
      sidePanelCollapsed,
      setShortcutHelpOpen,
    });
  });
}
